import { useState } from 'react';
import { Trans } from 'react-i18next';
import Cookie from 'js-cookie';
import styled from 'styled-components';

import { Button } from 'shared/components/Button';
import { useLocation } from 'shared/hooks/useLocation';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { GiftCard } from 'checkout/components/Payment/GiftCard';
import { ChangeCurrencyModal } from 'checkout/components/Payment/ChangeCurrencyModal';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { PaymentVendor } from 'checkout/enums/PaymentVendor';
import { STORAGE } from 'shared/enums/SitePreferences';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { t } from 'localization/utils/t';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { getRecommendedCurrency } from 'checkout/utils/getRecommendedCurrency';

const TP = 'checkout.components.Payment.v2.PayPal';

export const PayPal = () => {
  const {
    order: { number, wantId },
    createPayPalSession,
    sections: {
      payment: { loading: isPayPalLoading },
    },
  } = useCheckoutContext();
  const { user } = useStoreSelector((state) => state.auth);
  const { availablePaymentMethods } = useCheckoutContext();
  const { selectedCountryCode: countryCode } = useLocation();
  const [showChangeCurrencyModal, setShowChangeCurrencyModal] = useState(false);
  const isPayPalCanadaEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_PAYPAL_CANADA);
  const isPaymentAvailabilityServiceEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_PAYMENT_AVAILABILITY_SERVICE,
  );
  const isOffer = wantId !== undefined;

  const instructionsInfo = isOffer
    ? t(
        `${TP}.offerInstructionsInfo`,
        'You will be redirected to PayPal to place your offer securely.',
      )
    : t(
        `${TP}.orderInstructionsInfo`,
        'You will be redirected to PayPal to complete your purchase securely.',
      );
  const handleContinue = () => {
    const currencyCookie = Cookie.getJSON(STORAGE.CURRENCY);
    const recommendedCurrency = getRecommendedCurrency({
      userLocation: countryCode,
      userCurrency: currencyCookie,
      paymentVendor: PaymentVendor.PayPal,
      isOffer: !!wantId,
      isPaymentAvailabilityServiceEnabled,
      availablePaymentMethods,
    });

    const shouldChangeCurrency = isPayPalCanadaEnabled
      ? recommendedCurrency !== currencyCookie
      : currencyCookie !== 'USD';

    if (shouldChangeCurrency) {
      setShowChangeCurrencyModal(true);
    } else {
      void createPayPalSession({
        countryCode,
        email: user?.email,
        orderNumber: number?.toString(),
      });
    }
  };

  return (
    <PayPal.Wrapper>
      <GiftCard />
      <p>{instructionsInfo}</p>
      <p>
        <Trans
          i18nKey={`${TP}.payPalTerms`}
          defaults="By proceeding, you confirm you have read and agree to the <br> <purchasePolicyLink>Purchases and Returns Policy</purchasePolicyLink>."
          components={{
            purchasePolicyLink: (
              <PayPal.Link href={getRedirectionPath('/returns')} target="_blank" rel="noreferrer" />
            ),
          }}
        />
      </p>
      <Button
        data-qa="checkout_continue_paypal"
        loading={isPayPalLoading}
        disabled={isPayPalLoading}
        onClick={handleContinue}
      >
        {isOffer
          ? t(`${TP}.placeOfferPayPalButton`, 'Place Offer with PayPal')
          : t(`${TP}.placeOrderPayPalButton`, 'Place Order with PayPal')}
      </Button>
      {showChangeCurrencyModal && (
        <ChangeCurrencyModal
          onClose={() => setShowChangeCurrencyModal(false)}
          selectedPaymentVendor={PaymentVendor.PayPal}
          selectPaymentVendor={null}
        />
      )}
    </PayPal.Wrapper>
  );
};

PayPal.Wrapper = styled.div`
  width: 100%;

  p {
    margin-bottom: 16px;
  }
  button {
    margin-top: 48px;
  }
`;

PayPal.Link = styled.a`
  text-decoration: underline;
`;
